import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import "./imagesUpload.css";

/* ─── helper: resize in-browser ─── */
async function resizeImage(file, maxW = 1500, maxH = 1500, quality = 0.8) {
  const bmp = await createImageBitmap(file);
  let { width: w, height: h } = bmp;
  if (w > maxW || h > maxH) {
    const ratio = Math.min(maxW / w, maxH / h);
    w = Math.round(w * ratio);
    h = Math.round(h * ratio);
  }
  const canvas = new OffscreenCanvas(w, h);
  canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
  const blob = await canvas.convertToBlob({ type: "image/jpeg", quality });
  return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), { type: "image/jpeg" });
}

export default function FileUpload({
  username,
  eventId,
  uploadRoute,
  onUploadStart = () => {},
  onUploadComplete = () => {},
  userData,
}) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct,  setPct ] = useState(0);

  const galleryRef = useRef(null);
  const cameraRef  = useRef(null);
  const history    = useHistory();

  /* fake progress */
  useEffect(() => {
    if (!busy) return;
    const id = setInterval(() => setPct((p) => (p >= 99 ? 99 : p + 1)), 120);
    return () => clearInterval(id);
  }, [busy]);

  /* auto-upload */
  useEffect(() => {
    if (!file) return;
    onUploadStart();
    upload();
  }, [file]);

  /* choose file & resize if needed */
  const pickFile = async (e) => {
    const raw = e.target.files?.[0];
    if (!raw) return;

    const MAX = 6 * 1024 * 1024;          // 6 MB threshold
    let processed = raw;
    if (raw.size > MAX) {
      try {
        processed = await resizeImage(raw);
      } catch (err) {
        console.error("resize failed:", err);
      }
    }
    setFile(processed);
  };

  /* actual upload */
  const upload = async () => {
    try {
      setBusy(true);
      setPct(0);
  
      console.log("Uploading to:", uploadRoute);
      console.log("Event:", eventId, "Guest:", username);
      console.log("File:", file);
  
      const buffer = await file.arrayBuffer();
  
      const response = await fetch(uploadRoute, {
        method: "POST",
        headers: {
          "Content-Type": "application/octet-stream",
          "x-event-id": eventId,
          "x-guest-id": username
        },
        body: buffer
      });
  
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "Upload failed");
  
      const urls = (data.presigned_urls || []).sort();
      history.push("/gallery", { urls, userData });
    } catch (err) {
      console.error("Upload error:", err);
    } finally {
      setBusy(false);
      onUploadComplete();
    }
  };
  

  /* UI */
  return (
    <div className="file-upload">
      {/* hidden inputs */}
      <input ref={galleryRef} type="file" accept="image/*" style={{ display: "none" }} onChange={pickFile} />
      <input ref={cameraRef}  type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={pickFile} />

      {/* buttons */}
      <button className="circular-button button-comp" disabled={busy} onClick={() => cameraRef.current?.click()}>
        <div className="take-photo-txt">צלם תמונה</div>
        <img src="/icons/pick.png" alt="" className="take-photo" />
      </button>
      <button className="secondary-button circular-button button-comp" disabled={busy} onClick={() => galleryRef.current?.click()}>
        <div className="take-photo-txt">בחר מגלריה</div>
        <img src="/icons/upload.png" alt="" className="upload-photo" />
      </button>

      {/* progress bar */}
      {busy && (
        <div className="upload-progress">
          <div className="bar" style={{ width: `${pct}%` }} />
        </div>
      )}
    </div>
  );
}
